import { useLingui } from '@lingui/react/macro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { displayQuantity } from '@/utils/articleActionSpareParts';
import { useFetch } from '@/utils/fetch';

export type InvoiceLineFromGetMany =
  Endpoints['GET /invoice-lines']['response']['invoiceLines'][number];
export const useInvoiceLines = (
  params?: Endpoints['GET /invoice-lines']['query'],
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /invoice-lines']>();

  return useQuery({
    queryKey: ['invoice-lines', params],
    queryFn: () => fetch('/invoice-lines', params),
    enabled: options?.enabled ?? true,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useInvoiceLineName = (
  invoiceLine: Parameters<ReturnType<typeof useGetInvoiceLineName>>[0]
) => {
  const getInvoiceLineName = useGetInvoiceLineName();

  return getInvoiceLineName(invoiceLine);
};

export const useGetInvoiceLineName = () => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  return (invoiceLine: {
    price: {
      articleAction: null | {
        actionTypeOrganization: null | { name: I18nWording };
        packActionTypeOrganization: null | { name: I18nWording };
        customDescription: null | string;
      };
      articleActionSparePart: null | {
        quantity: number;
        sparePartType: { name: I18nWording; unit: I18nWording | null };
      };
      articleExchange: null | {
        id: string;
      };
      replacementArticle: null | {
        id: string;
      };
      fee: null | {
        feeType: {
          name: I18nWording;
        };
      };
      deposit: null | {
        id: string;
        percentage: number | null;
        type: 'percentage' | 'paid-estimate-advance';
      };
      creditTransactions: { id: string }[];
    };
  }) => {
    if (invoiceLine.price.articleAction) {
      if (invoiceLine.price.articleAction.actionTypeOrganization) {
        return _db(invoiceLine.price.articleAction.actionTypeOrganization.name);
      } else if (invoiceLine.price.articleAction.packActionTypeOrganization) {
        return _db(invoiceLine.price.articleAction.packActionTypeOrganization.name);
      } else {
        return invoiceLine.price.articleAction.customDescription ?? '';
      }
    } else if (invoiceLine.price.articleActionSparePart) {
      return `${_db(invoiceLine.price.articleActionSparePart.sparePartType.name)} ${displayQuantity(invoiceLine.price.articleActionSparePart, _db)}`;
    } else if (invoiceLine.price.fee) {
      return _db(invoiceLine.price.fee.feeType.name);
    } else if (invoiceLine.price.articleExchange || invoiceLine.price.replacementArticle) {
      return t({ id: 'invoice-line.name.exchange', message: 'Exchange' });
    } else if (invoiceLine.price.deposit) {
      const label =
        invoiceLine.price.deposit.type === 'percentage'
          ? t({ id: 'invoice-line.name.deposit', message: 'Deposit' })
          : t({ id: 'invoice-line.name.paid-estimate-advance', message: 'Paid estimate advance' });
      const percentage = invoiceLine.price.deposit.percentage;

      if (percentage) {
        const percentageLabel = t({
          id: 'invoice-line.name.deposit.percentage',
          message: `${percentage}% services`,
        });

        return `${label} (${percentageLabel})`;
      } else {
        return label;
      }
    } else if (invoiceLine.price.creditTransactions.length) {
      return t({ id: 'invoice-line.name.amount-paid', message: 'Amount paid' });
    } else {
      return '';
    }
  };
};

export const getAmountWithQuantity = (price: {
  amount: number;
  articleActionSparePart: null | { quantity: number };
}) => {
  return price.articleActionSparePart
    ? price.amount * price.articleActionSparePart.quantity
    : price.amount;
};

export const getIssuerName = (invoiceLine: InvoiceLineFromGetMany) => {
  return invoiceLine.price.destinationCompany && invoiceLine.price.destinationEntity
    ? `${invoiceLine.price.destinationCompany.name} (${invoiceLine.price.destinationEntity.name})`
    : undefined;
};

export const getRecipientName = (invoiceLine: InvoiceLineFromGetMany) => {
  return invoiceLine.price.originCompany && invoiceLine.price.originEntity
    ? `${invoiceLine.price.originCompany.name} (${invoiceLine.price.originEntity.name})`
    : undefined;
};
